import React from "react";
import { v4 as uuidv4 } from "uuid";
import { Button } from "@org/ui";
import { Checkbox } from "@org/ui";
import { Input } from "@org/ui";
import { Label } from "@org/ui";
import { TextArea } from "@org/ui";
import { XIcon } from "lucide-react";
import { MultipleChoiceExerciseContent, MultipleChoiceOptionContent } from "@org/ui";
import { ExerciseEditorProps } from "./exercise-editor-types";

export const MultipleChoiceExerciseEditor: React.FC<ExerciseEditorProps<MultipleChoiceExerciseContent>> = ({
  content,
  updateContent
}) => {
  const rawOptions = content.data.options ?? [];

  const handleUpdate = (nextData: Partial<MultipleChoiceExerciseContent["data"]>) => {
    updateContent({
      ...content,
      data: {
        ...content.data,
        ...nextData
      }
    });
  };

  const normalizeOption = React.useCallback(
    (option: MultipleChoiceOptionContent | string, index: number): MultipleChoiceOptionContent => {
      const fallbackCorrectIndex =
        typeof content.data.correctOptionIndex === "number"
          ? Math.max(0, Math.min(content.data.correctOptionIndex, rawOptions.length - 1))
          : 0;

      if (typeof option === "string") {
        return {
          optionId: undefined,
          label: option,
          isCorrect: index === fallbackCorrectIndex,
          order: index,
        };
      }

      const optionId =
        option?.optionId ??
        (option as any)?.multipleChoiceOptionId ??
        (option as any)?.id ??
        undefined;

      const label =
        typeof option?.label === "string"
          ? option.label
          : option?.label != null
            ? String(option.label)
            : "";

      const order = typeof option?.order === "number" ? option.order : index;

      const isCorrect =
        typeof option?.isCorrect === "boolean"
          ? option.isCorrect
          : index === fallbackCorrectIndex;

      return {
        optionId,
        label,
        isCorrect,
        order,
      };
    },
    [content.data.correctOptionIndex, content.id, rawOptions.length],
  );

  const normalizedOptions = React.useMemo(() => {
    const options = rawOptions.map((option, index) => normalizeOption(option, index));
    options.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

    if (!options.some((option) => option.isCorrect) && options.length) {
      options[0] = { ...options[0], isCorrect: true };
    }

    return options.map((option, index) => ({
      ...option,
      order: index,
    }));
  }, [normalizeOption, rawOptions]);

  React.useEffect(() => {
    const needsNormalization =
      rawOptions.length !== normalizedOptions.length ||
      rawOptions.some((option, index) => {
        const normalized = normalizedOptions[index];
        if (typeof option === "string") {
          return true;
        }
        return (
          option?.optionId !== normalized.optionId ||
          option?.label !== normalized.label ||
          Boolean(option?.isCorrect) !== normalized.isCorrect ||
          (option?.order ?? index) !== normalized.order
        );
      });

    if (needsNormalization) {
      handleUpdate({
        options: normalizedOptions,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [normalizedOptions, rawOptions]);

  const withNormalizedOrder = (options: MultipleChoiceOptionContent[]) =>
    options.map((option, index) => ({
      ...option,
      order: option.order ?? index,
    }));

  const updateOptions = (updater: (options: MultipleChoiceOptionContent[]) => MultipleChoiceOptionContent[]) => {
    const nextOptions = withNormalizedOrder(updater(normalizedOptions));
    const hasCorrect = nextOptions.some((option) => option.isCorrect);
    if (!hasCorrect && nextOptions.length > 0) {
      nextOptions[0] = { ...nextOptions[0], isCorrect: true };
    }

    handleUpdate({
      options: nextOptions,
    });
  };

  const handleOptionChange = (index: number, value: string) => {
    updateOptions((options) =>
      options.map((option, idx) => (idx === index ? { ...option, label: value } : option)),
    );
  };

  const handleRemoveOption = (index: number) => {
    if (normalizedOptions.length <= 2) return;
    updateOptions((options) => options.filter((_, idx) => idx !== index));
  };

  const handleAddOption = () => {
    updateOptions((options) => [
      ...options,
      {
        optionId: uuidv4(),
        label: "",
        isCorrect: false,
        order: options.length,
      },
    ]);
  };

  const handleCorrectOptionToggle = (index: number, checked: boolean) => {
    updateOptions((options) =>
      options.map((option, idx) =>
        idx === index
          ? {
              ...option,
              isCorrect: checked,
            }
          : option,
      ),
    );
  };

  return (
    <div className="space-y-4">
      <div>
        <Label htmlFor={`correction-${content.id}`}>Correction</Label>
        <TextArea
          id={`correction-${content.id}`}
          value={content.data.correction}
          onChange={(event) => handleUpdate({ correction: event.target.value })}
          placeholder="Optional note on how to evaluate the answer"
          className="mt-1"
        />
      </div>

      <div>
        <Label htmlFor={`question-${content.id}`}>Question</Label>
        <TextArea
          id={`question-${content.id}`}
          value={content.data.question}
          onChange={(event) => handleUpdate({ question: event.target.value })}
          placeholder="Enter the question"
          className="mt-1"
        />
      </div>

      <div className="space-y-2">
        <Label>Options</Label>
        <div className="space-y-2">
          {normalizedOptions.map((normalized, index) => {
            const optionId = normalized.optionId ?? `option-${content.id}-${index}`;
            const isSelected = !!normalized.isCorrect;

            return (
              <label
                key={optionId}
                htmlFor={`${optionId}-checkbox`}
                className={`group flex items-center gap-3 rounded-lg border p-3 transition-all ${
                  isSelected
                    ? "border-emerald-500 bg-emerald-50 shadow-sm ring-2 ring-emerald-200"
                    : "border-slate-200 hover:border-emerald-300 hover:bg-emerald-50/30"
                }`}
              >
                <Checkbox
                  id={`${optionId}-checkbox`}
                  className="border-slate-300 data-[state=checked]:border-emerald-600 data-[state=checked]:bg-emerald-600"
                  checked={isSelected}
                  onCheckedChange={(checked) => handleCorrectOptionToggle(index, checked === true)}
                />
                <Input
                  value={normalized.label}
                  placeholder={`Option ${index + 1}`}
                  onChange={(event) => handleOptionChange(index, event.target.value)}
                  className="flex-1"
                />
                {normalizedOptions.length > 2 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-slate-500 hover:text-red-500"
                    onClick={() => handleRemoveOption(index)}
                  >
                    <XIcon className="h-4 w-4" />
                  </Button>
                )}
              </label>
            );
          })}
        </div>

        <Button
          variant="outline"
          size="sm"
          onClick={handleAddOption}
        >
          Add Option
        </Button>
      </div>
    </div>
  );
};
